/**
 * Builds a display name from structured name parts.
 *
 * Format examples:
 * - "Dr. John Smith, PhD" (prefix + first + last + suffix)
 * - "Dr. John Smith"      (prefix + first + last)
 * - "John Smith, J.D."    (first + last + suffix)
 * - "John Smith"          (first + last)
 *
 * The suffix is preceded by a comma when present.
 */
export function buildDisplayName(parts: {
  honorificPrefix?: string | null;
  firstName: string;
  lastName: string;
  honorificSuffix?: string | null;
}): string {
  const prefix = parts.honorificPrefix?.trim() || '';
  const first = parts.firstName.trim();
  const last = parts.lastName.trim();
  const suffix = parts.honorificSuffix?.trim() || '';

  const namePart = [prefix, first, last].filter(Boolean).join(' ');

  if (suffix) {
    return `${namePart}, ${suffix}`;
  }

  return namePart || 'User';
}

/**
 * Formats a name in directory/roster style: "(Prefix?) Last, First (, Suffix?)".
 * Used for table cells in the admin Users and Attendees pages where alphabetical
 * lastName ordering is the read pattern.
 *
 * Format examples:
 * - "Dr. Smith, John, PhD" (prefix + first + last + suffix)
 * - "Smith, John, PhD"     (first + last + suffix)
 * - "Dr. Smith, John"      (prefix + first + last)
 * - "Smith, John"          (first + last)
 * - "Smith"                (last only)
 * - "John"                 (first only)
 * - fallback               (no name parts; defaults to "User" if unset)
 *
 * Distinct from `buildDisplayName` because that helper drives the stored
 * `displayName` field on contacts/users (first-name-first); changing it
 * would silently mutate every existing row on next write.
 */
export function formatDirectoryName(parts: {
  honorificPrefix?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  honorificSuffix?: string | null;
  fallback?: string;
}): string {
  const prefix = parts.honorificPrefix?.trim() || '';
  const first = parts.firstName?.trim() || '';
  const last = parts.lastName?.trim() || '';
  const suffix = parts.honorificSuffix?.trim() || '';

  let core: string;
  if (last && first) {
    core = `${last}, ${first}`;
  } else if (last) {
    core = last;
  } else if (first) {
    core = first;
  } else {
    return parts.fallback ?? 'User';
  }

  const head = prefix ? `${prefix} ${core}` : core;
  return suffix ? `${head}, ${suffix}` : head;
}

/**
 * Directory sort order: last name, then first name.
 *
 * Every attendee list should use this. Firestore returns `documentId in [...]`
 * results ordered by document ID, so an unsorted list of Person docs comes back
 * ordered by personId — which reads as random to a user. Session Detail and
 * Group Detail both shipped that way until 2026-07-25.
 *
 * Honorifics are deliberately excluded: sorting on the rendered string would
 * file "Dr. Kim, Sarah" under D.
 */
export function compareByDirectoryName(
  a: { firstName?: string | null; lastName?: string | null },
  b: { firstName?: string | null; lastName?: string | null },
): number {
  const last = (a.lastName ?? '').localeCompare(b.lastName ?? '');
  if (last !== 0) return last;
  return (a.firstName ?? '').localeCompare(b.firstName ?? '');
}
